import { Box, CircularProgress } from '@mui/material';
import CustomText, { textStyles } from "../../../components/CustomText";
import CustomButton from "../../../components/CustomButton";
import BaseCard from '../../../components/Cards/Card';
import CustomEmailInput from '../../../components/EmailInput';
import CustomPasswordInput from "../../../components/PasswordInput";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from '@mui/material/useMediaQuery';
import { useFormik } from 'formik';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState, useMemo, useEffect } from 'react';
import { BASE_URL } from '../../../components/BaseUrl';
import { useDispatch, useSelector } from 'react-redux';
import { setCredentials, setOtpData } from '../../../Redux/Slices/AuthSlice';
import SelectDropdown from '../../../components/SingleSelectDropdown'; // ✅ use your fixed single-select
import { getApiMessage } from '../../../utils/messageHandler';

/* -------------------- RULE ICON --------------------- */
const RuleRow = ({ ok, label, showError }) => {
  const textColor = ok
    ? '#16a34a'
    : showError
      ? '#d32f2f'
      : '#64748b';

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
      <span
        aria-hidden
        style={{
          display: 'inline-flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: 18,
          height: 18,
          borderRadius: '50%',
          border: ok ? 'none' : '2px solid #9aa4b2',
          background: ok ? '#22c55e' : 'transparent'
        }}
      >
        {ok ? (
          <span
            style={{
              width: 6,
              height: 10,
              borderRight: '2px solid #fff',
              borderBottom: '2px solid #fff',
              transform: 'rotate(45deg)',
            }}
          />
        ) : (
          <span style={{ position: 'relative', width: 10, height: 10 }}>
            <span
              style={{
                position: 'absolute',
                width: '100%',
                height: 2,
                background: showError ? '#d32f2f' : '#9aa4b2',
                transform: 'rotate(45deg)',
                top: '50%',
                left: 0,
                marginTop: -1,
              }}
            />
            <span
              style={{
                position: 'absolute',
                width: '100%',
                height: 2,
                background: showError ? '#d32f2f' : '#9aa4b2',
                transform: 'rotate(-45deg)',
                top: '50%',
                left: 0,
                marginTop: -1,
              }}
            />
          </span>
        )}
      </span>
      <span style={{ fontSize: 14, color: textColor }}>{label}</span>
    </Box>
  );
};

/* -------------------- PASSWORD CHECKLIST --------------------- */
const PasswordChecklist = ({ password, showError }) => {
    const { t } = useTranslation();

  const rules = useMemo(() => ({
    length: password.length >= 8,
    upper: /[A-Z]/.test(password),
    lower: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
    special: /[!@#$%^&*()[\]{}\-_+=~`|:;"'<>,.?/\\]/.test(password),
  }), [password]);

  return (
<Box sx={{ mt: 1, mb: { xs: 2, sm: 1.5 } }}>
  <RuleRow ok={rules.length} label={t('passwordRules.minLength')} showError={showError && !rules.length}/>
  <RuleRow ok={rules.upper} label={t('passwordRules.upperCase')} showError={showError && !rules.upper}/>
  <RuleRow ok={rules.lower} label={t('passwordRules.lowerCase')} showError={showError && !rules.lower}/>
  <RuleRow ok={rules.number} label={t('passwordRules.number')} showError={showError && !rules.number}/>
  <RuleRow ok={rules.special} label={t('passwordRules.special')} showError={showError && !rules.special}/>
</Box>
  );
};

/* -------------------- REGISTER --------------------- */
const Registeration = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useDispatch();
  const profileUpdateToken =
    useSelector((s) => s.auth.profileUpdateToken) || localStorage.getItem("profileUpdateToken");

  // State for dropdowns
  const [companies, setCompanies] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [loadingDepartments, setLoadingDepartments] = useState(false);

  // validation function
  const validate = (values) => {
    const errors = {};

    // Commented out name validation
    // if (!values.firstName?.trim()) {
    //   errors.firstName = t("register.errors.firstNameRequired") || "First name is required";
    // }
    // if (!values.lastName?.trim()) {
    //   errors.lastName = t("register.errors.lastNameRequired") || "Last name is required";
    // }

    if (!values.email) {
      errors.email = t("register.errors.emailRequired") || "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) {
      errors.email = t("register.errors.emailInvalid") || "Invalid email address";
    }

    if (!values.confirmPassword) {
  errors.confirmPassword = t("register.errors.confirmPasswordRequired") || "Please confirm your password";
} else if (values.password !== values.confirmPassword) {
  errors.confirmPassword = t("register.errors.passwordMismatch") || "Passwords do not match";
}

    const pwd = values.password || "";
    const hasLen = pwd.length >= 8;
    const hasLower = /[a-z]/.test(pwd);
    const hasUpper = /[A-Z]/.test(pwd);
    const hasNum = /[0-9]/.test(pwd);
    const hasSpecial = /[!@#$%^&*()[\]{}\-_+=~`|:;"'<>,.?/\\]/.test(pwd);

    if (!pwd) {
      errors.password = t("register.errors.passwordRequired") || "Password is required";
    } else if (!(hasLen && hasLower && hasUpper && hasNum && hasSpecial)) {
      errors.password = t("register.errors.passwordInvalid") || "Password must meet all requirements.";
    }

    return errors;
  };

  const formik = useFormik({
    initialValues: { email: "", password: "",confirmPassword: "" },
    validate,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: async (values) => {
      const errors = validate(values);
      if (Object.keys(errors).length > 0) {
        formik.setTouched({
          email: true,
          password: true,
        });
        const key = Object.keys(errors)[0];
        toast.error(errors[key]);
        return;
      }

      setIsSubmitting(true);
      try {
        const response = await fetch(`${BASE_URL}/public/worker/register-basic`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: values.email,
            password: values.password,
          }),
        });

        const data = await response.json();
        console.log("📩 Full API Response:", data);

        if (!response.ok || data.error) {
          toast.error(getApiMessage(data, "Registration failed"));
          return;
        }

        const worker = data?.data?.worker;
        const verification = data?.data?.verification;

        dispatch(setCredentials({
          email: worker?.email,
          workerId: worker?.id,
        }));

        dispatch(setOtpData({
          email: worker?.email,
          userId: worker?.id,
          otpToken: verification?.otpToken,
          verificationCode: verification?.verificationCode,
          verificationLink: verification?.verificationLink,
          expiresIn: verification?.expires_in,
        }));
// console.log(data)
        toast.success(getApiMessage(data, "Registration successful"), {
          id: "registration-success",
        });
        setTimeout(() => navigate("/otp-screen"), 1000);
      } catch (err) {
        const errorMsg = err.response?.data 
          ? getApiMessage(err.response.data, "Something went wrong")
          : (err.message || "Something went wrong");
        toast.error(errorMsg);
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  /* -------------------- FETCH COMPANIES --------------------- */
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        setLoadingCompanies(true);
        const res = await fetch(`${BASE_URL}/public/companies?no_pagination=true`);
        const data = await res.json();
        if (!res.ok || data.error) throw new Error(data.message);
        const opts = data.data.companies.map((c) => ({
          label: `${c.legal_name} (${c.business_email})`,
          value: c.id,
        }));
        setCompanies(opts);
      } catch (err) {
        const errorMsg = err.response?.data 
          ? getApiMessage(err.response.data, "Failed to load companies")
          : (err.message || "Failed to load companies");
        toast.error(errorMsg);
      } finally {
        setLoadingCompanies(false);
      }
    };
    fetchCompanies();
  }, []);

  /* -------------------- FETCH DEPARTMENTS --------------------- */
  useEffect(() => {
    if (!selectedCompany) return;

    const fetchDepartments = async () => {
      try {
        setLoadingDepartments(true);
        const res = await fetch(
          `${BASE_URL}/public/departmentsByCompId/${selectedCompany}`
        );
        const data = await res.json();
        if (!res.ok || data.error) throw new Error(data.message);
              const departmentsList = data.data?.departments || [];

      if (departmentsList.length === 0) {
        toast.error("No department found in this company");
      }
      
        const opts = data.data.departments.map((d) => ({
          label: d.name,
          value: d.id,
        }));
        setDepartments(opts);
      } catch (err) {
        const errorMsg = err.response?.data 
          ? getApiMessage(err.response.data, "Failed to load departments")
          : (err.message || "Failed to load departments");
        toast.error(errorMsg);
      } finally {
        setLoadingDepartments(false);
      }
    };
    fetchDepartments();
  }, [selectedCompany]);

  /* --------- helper for borders (touched + error) --------- */
  const isError = (name) => formik.touched[name] && formik.errors[name];
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  return (
    <Box sx={{ backgroundColor: "#EFF4F8", pt: 0, mt: 5, py: 5 }}>
      <Box
        sx={{
          px: 2,
          display: "flex",
          justifyContent: "center",
          minHeight: "100vh",
          overflowY: "auto",
          alignItems: "center",
        }}
      >
        <BaseCard
          maxWidth={isMobile ? "100%" : 450}
          sx={{ width: isMobile ? "100%" : "auto", px: isMobile ? 2 : 0 }}
        >
          <form onSubmit={formik.handleSubmit} noValidate>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.8, mb: 2 }}>
                <CustomText
                  sx={{
                    ...textStyles.h4,
                    color: theme.palette.tertiary.main,
                    letterSpacing: "0px !important",
                  }}
                >
                  {t("register.title")}
                </CustomText>
                <CustomText
                  sx={{
                    ...textStyles.body4,
                    color: theme.palette.text.primary,
                    letterSpacing: "0px !important",
                  }}
                >
                  {t("register.subtitle")}
                </CustomText>
              </Box>

          

              {/* Email */}
              <label style={{ marginTop: 5 }}>{t("register.emailLabel")}</label>
              <CustomEmailInput
                name="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email ? formik.errors.email : ""}
              />

       

              {/* Password */}
              <label style={{ marginTop: 5 }}>{t("register.passwordLabel")}</label>
              <CustomPasswordInput
                 placeholder="********"
                name="password"
                value={formik.values.password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.password && Boolean(formik.errors.password)}
              />

              {/* Checklist */}
        


<label style={{ marginTop: 5 }}>{t("register.confirmPasswordLabel")}</label>
<CustomPasswordInput
  placeholder="********"
  name="confirmPassword"
  value={formik.values.confirmPassword}
  onChange={formik.handleChange}
  onBlur={formik.handleBlur}
  error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
/>
{formik.touched.confirmPassword && formik.errors.confirmPassword && (
  <span style={{ color: "red", fontSize: "12px" }}>
    {formik.errors.confirmPassword}
  </span>
)}

      <PasswordChecklist password={formik.values.password} />

         <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, mb: 1 }}>
                <input
                  type="checkbox"
                  id="acceptTerms"
                  checked={acceptedTerms}
                  onChange={e => setAcceptedTerms(e.target.checked)}
                  style={{ marginRight: 8, width: 18, height: 18 }}
                  required
                />
                <span
  style={{
 color: "#1976D2",
    cursor: "pointer",
    textDecoration: "underline",
    fontSize: 14,
  }}
  onClick={() => window.open("https://biometricpro.app/terms", "_blank")}
>
  {t("acceptTermsAndConditions") || "I accept the Terms and Conditions"}
</span>

              </Box>

              <CustomButton
                label={
                  isSubmitting ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CircularProgress size={18} sx={{ color: "#fff" }} />
                      <span>{t("register.continue")}</span>
                    </Box>
                  ) : (
                    t("register.continue")
                  )
                }
                type="submit"
                fullWidth
                                disabled={!acceptedTerms || isSubmitting}

                sx={{ mt: 2 }}
              />
            </Box>
          </form>
        </BaseCard>
      </Box>
    </Box>
  );
};

export default Registeration;

















